import { AfterViewInit, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { BehaviorSubject, ReplaySubject, Subscription } from 'rxjs';
import { ACCESS_TOKEN, REFRESH_TOKEN } from '../../../storage/constants.lib';
import { AuthService } from '../../../services/authentication/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { RESET_PASSWORD_DIALOG_CONFIG, ResetPasswordDialogComponent, ResetPasswordDialogModel } from '../../../dialogs/reset-password-dialog/reset-password-dialog.component';
import { FormsModule } from '@angular/forms';
import { OverlayLoadingDirective } from '../../../directives/overlay-loading.directive';
import { PasswordInputComponent } from '../../field-components/password-input/password-input.component';
import { MatButton } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AppStore } from '../../../store/app.store';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    imports: [FormsModule, OverlayLoadingDirective, MatButton, MatIconModule, PasswordInputComponent]
})
export class LoginComponent implements OnInit, OnDestroy,AfterViewInit {

  linkSent = false;
  busy: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  username = '';
  password = '';
  loginError = false;
  disabledAccount = false;
  mobileOnly = false;
  browser = '';
  userRequired = false;
  blocked = false;

  private store = inject(AppStore);

  private subscriptions: Subscription[] = [];
  @ViewChild('loginButton') loginButton;
  @ViewChild('passwordInput') passwordInput;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    public dialog: MatDialog,
  ) {
    this.route.queryParams.subscribe(params => {
      this.username = params['user'];
    });
  }

  ngAfterViewInit() {
    this.loginButton.nativeElement.focus();
  }

  ngOnInit(): void {
    
    document.getElementById('loginsplogo').style.display = 'block';
    document.getElementById('header').style.backgroundColor = '#003f9c';



    this.subscriptions.push(this.authService.user$.subscribe((x) => {
      if (this.route.snapshot.url[0].path === 'login') {

        const accessToken = localStorage.getItem(ACCESS_TOKEN);
        let refreshToken = localStorage.getItem(REFRESH_TOKEN);
        if (refreshToken != null) {
          this.authService.refreshToken().subscribe(() => {
            if (x && accessToken && refreshToken) {
              refreshToken = localStorage.getItem(REFRESH_TOKEN);
              this.router.navigate(["/insights"]);
            }
          });

        }

      }
    }));

    if (this.router.url.includes('/forgotpassword')) {
      const qp = this.route.snapshot.queryParamMap;
      const dialogData: ResetPasswordDialogModel = {
        key: qp.get('key'),
        orgPolicyDays: qp.has('orgPolicyDays') ? Number(qp.get('orgPolicyDays')) || null : null,
        passwordInsecure: qp.get('passwordInsecure') === 'true'
      };

      this.dialog
        .open<ResetPasswordDialogComponent, ResetPasswordDialogModel, boolean>(
          ResetPasswordDialogComponent,
          { ...RESET_PASSWORD_DIALOG_CONFIG,data: dialogData }
        )
        .afterClosed()
        .subscribe((success) => {
          if (success) this.router.navigate(['/insights']);
        });
    }
  }

  sendForgotLink() {
    this.loginError = false;

    if (!this.username || this.username == '') {
      this.userRequired = true;
      return;
    }

    this.authService
      .forgotPassword(this.username)
      .pipe(finalize(() => (this.busy.next(false))))
      .subscribe(
        () => {
          this.loginError = false;
          this.linkSent = true;
          this.userRequired = false;
        },
        error => {
          this.loginError = true;
        },
        () => {
          this.linkSent = true;
          this.userRequired = false;
        }
      );
  }

  login() {
    this.loginError = false;
    this.linkSent = false;
    this.userRequired = false;
    this.blocked = false;
    this.disabledAccount = false;
    this.mobileOnly = false;

    if (!this.username || this.username == '') {
      this.userRequired = true;
      return;
    }

    if ( !this.password) {
      return;
    }
    this.busy.next(true);

    this.store.setUsername(this.username);

    this.authService
      .login(this.username, this.password, true)
      .pipe(finalize(() => (this.busy.next(false))))
      .subscribe(
        (x) => {
          if (x.ResetPassword && x.Insecure) {
            this.router.navigate(['/forgotpassword'], {queryParams: {key: x.Key, orgPolicyDays: null, passwordInsecure: x.Insecure}});
          }
          else if (x.ResetPassword) {
            this.router.navigate(['/forgotpassword'], {queryParams: {key: x.Key, orgPolicyDays: x.OrgPolicyDays, passwordInsecure: x.Insecure ?? false}});
          }
          else {
            this.router.navigate(["/insights"]);
          }

        },
        (x) => {
          const y = x;
          if (x == "Blocked Account")
          {
            this.blocked = true;
          }
          else if (x == "Invalid Credentials")
          {
            this.disabledAccount = false;
            this.mobileOnly = false;
            this.loginError = true;
            this.userRequired = false;
            this.blocked = false;
          }
          else if (x == "Disabled Account") {
            this.disabledAccount = true;
          }
          else if (x == "Mobile Only") {
            this.mobileOnly = true;
          }
          else {
            this.disabledAccount = false;
            this.mobileOnly = false;
            this.loginError = true;
            this.userRequired = false;
            this.blocked = false;
          }
        }
      );
  }

  ngOnDestroy() {
    // unsubscribe to ensure no memory leaks
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }
}
